import { FlexColumn, FlexResponsive, LabeledField, LabeledInput } from '@gorila-shared-ui/components';
import { useEffect } from 'react';
import { ChromePicker } from 'react-color';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { REQUIRED_FIELD_TEXT } from '../../../constants/app';
import { useForms } from '../../../hooks/useForms';
import { useStyles } from '../../../hooks/useStyles';
import {
  editActivityState,
  editActivityTouchedState,
  editActivityValidState,
  jobActivityDetailState,
  selectedJobActivityIdState,
} from '../../../storage/jobTypesStates';
import { JobActivityRequestTouched, JobTypeActivityRequest } from '../../../types/jobs';
import { FormSelect } from '../../forms/FormSelect';
import { LabeledTextArea } from '../../ui/LabeledTextArea';
import { JobStatusSelect } from '../JobStatusSelect';
import { JobActivitySelect } from './JobActivitySelect';

export function JobActivityFields() {
  const { jobTypeId } = useParams();
  const selectedActivityId = useRecoilValue(selectedJobActivityIdState);
  const jobActivity = useRecoilValue(jobActivityDetailState);
  const { css, theme } = useStyles();
  const [form, setForm] = useRecoilState(editActivityState);
  const [formTouched, setFormTouched] = useRecoilState(editActivityTouchedState);
  const resetFormTouchedState = useResetRecoilState(editActivityTouchedState);
  const resetForm = useResetRecoilState(editActivityState);
  const formValid = useRecoilValue(editActivityValidState);
  const { onTouched } = useForms<JobTypeActivityRequest, JobActivityRequestTouched>(setForm, setFormTouched);

  useEffect(() => {
    resetFormTouchedState();
    return () => {
      resetForm();
      resetFormTouchedState();
    };
  }, []);

  const onChangeInput = (key: keyof JobTypeActivityRequest, value: any) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onChangeStatusInput = (value: any) => {
    setForm((prev) => {
      const prevJobStatus = prev.jobStatus;
      return {
        ...prev,
        jobStatus: {
          ...prevJobStatus!,
          ...value,
        },
      };
    });
  };

  if (!jobTypeId) return null;

  return (
    <FlexColumn
      gap={theme.sizing.scale500}
      classNames={css({
        overflow: 'auto',
        height: '100%',
      })}
    >
      <FlexResponsive>
        <FlexColumn classNames={css({ flex: 1 })}>
          <LabeledInput
            label="Nombre"
            placeholder="Ingresa el nombre"
            value={form.name}
            onChange={(value) => {
              onChangeInput('name', value);
            }}
            onBlur={() => onTouched('nameTouched')}
            error={formTouched.nameTouched && !formValid.name && REQUIRED_FIELD_TEXT}
            required
          />
          <LabeledInput
            label="Orden"
            placeholder="Ingresa el orden"
            value={form.order}
            onChange={(value) => {
              onChangeInput('order', value);
            }}
            onBlur={() => onTouched('orderTouched')}
            error={formTouched.orderTouched && !formValid.order && REQUIRED_FIELD_TEXT}
            required
          />
          <JobStatusSelect
            label="Estatus"
            selectedJobStatusId={`${form.jobStatus?.statusType}`}
            onChangeJobStatus={(value) => {
              onChangeStatusInput({
                ...value,
              });
            }}
            onBlur={() => onTouched('jobStatusTouched')}
            error={formTouched.jobStatusTouched && !formValid.jobStatus && REQUIRED_FIELD_TEXT}
            required
          />
        </FlexColumn>
        <FlexColumn>
          <LabeledField
            label="Color del Estado"
            required
          >
            <ChromePicker
              disableAlpha
              color={form.jobStatus?.color || '#000000'}
              onChange={(color) => {
                onChangeStatusInput({ color: color.hex });
              }}
            />
          </LabeledField>
        </FlexColumn>
      </FlexResponsive>
      <LabeledTextArea
        label="Descripción"
        placeholder="Ingresa la descripción "
        value={form.description}
        onChangeValue={(value) => {
          onChangeInput('description', value);
        }}
        size="large"
      />
      <FormSelect
        label="Formularios"
        selectedFormIds={form.forms?.length > 0 ? form.forms : undefined}
        forms={jobActivity?.forms}
        onChangeFormIds={(value) => {
          onChangeInput('forms', value);
        }}
        isMulti
      />
      <JobActivitySelect
        jobTypeId={jobTypeId}
        label="Actividad siguiente"
        selectedActivityIds={form.nextActivities?.length > 0 ? form.nextActivities : undefined}
        activities={jobActivity?.nextActivities}
        onChangeActivityIds={(value) => {
          onChangeInput('nextActivities', value);
        }}
        omitActivityIdOption={selectedActivityId}
        caption={'Una vez creadas las siguientes actividades, recuerde volver aqui para seleccionarlas'}
        isMulti
      />
    </FlexColumn>
  );
}
